import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppSelector, useAppDispatch } from "store/store";
import { testEnvMaxDuration } from "config/constant";
import { ProctoringRoute } from "routes/route";
import { AlertLogConfig, SessionStorage } from "services/session-token";
import {
  AlvyEventState,
  AlvyEventTypes,
  MediaRecorderState,
  ScreenShareStatus
} from "globals/enums";

// Assets & Components
import Button from "components/Button";
import FooterLogo from "components/FooterLogo";
import Header from "components/Header";
import ViewDemo from "components/ViewDemo";
import Countdown from "components/Countdown";
import VideoPlayer from "components/VideoPlayer";

import Loader from "assets/img/loader_green.gif";

// Hooks
import useMediaRecorder from "hooks/useMediaRecorder";

// Services
import { useCreateBlobMutation } from "services/blobStorage";
import { useCreateAlertMutation } from "services/alert";

import { GlobalAppState } from "globals/interfaces";
import { nextScreen } from "routes/workflow";
import { alertHelper } from "../helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import Base from "components/layouts/Base";
import Content from "components/layouts/Content";
import { MediaContext } from "context/MediaContext";
import { getSystemTime, trackEventInAlvy } from "helpers/common";
import { handleError } from "helpers/sentry";
import { guardedStepNavigation } from "helpers/guarded-step-navigation";

export type RecorderState = RecordingState | MediaRecorderState.Complete | undefined;

const EnvTestCapture = () => {
  const globalState: GlobalAppState = useAppSelector((state) => state.app);
  const screenShareStatus = useAppSelector((state) => state.screenShare.screenShareStatus);
  const screenShareStatusRef = useRef<ScreenShareStatus | "">(screenShareStatus);

  useEffect(() => {
    screenShareStatusRef.current = screenShareStatus;
  }, [screenShareStatus]);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [
    createBlob,
    {
      isLoading: blobCreationInProgress,
      isError: blobCreationError
    }
  ] = useCreateBlobMutation();
  const [createAlert] = useCreateAlertMutation();
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { mediaStream } = useContext(MediaContext);
  const [isDemoOpened, SetIsDemoOpened] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const storage = useSelector((state: RootState) => state.app.storage);
  const isSubmitDisabled = isSubmitted || blobCreationInProgress;

  const proceedToNextScreen = useCallback(() => {
    const getNextScreen = nextScreen(
      ProctoringRoute.TestEnvCapture,
      globalState.configuration,
      globalState.session.session_type
    );
    guardedStepNavigation({
      sourceRoute: ProctoringRoute.TestEnvCapture,
      destinationRoute: getNextScreen,
      screenShareStatus: screenShareStatusRef.current,
      dispatch,
      navigate
    });
  }, [globalState.configuration, globalState.session.session_type, dispatch, navigate]);

  const handleStopRecording = () => {
    console.log("Recorder state", recorderState, recorder?.state);
    if (recorder?.state === MediaRecorderState.Recording) {
      recorder?.stop();
    }
    SetRecorderState(MediaRecorderState.Complete);
  };

  const [recorder, data] = useMediaRecorder(mediaStream, handleStopRecording, testEnvMaxDuration);
  const [recorderState, SetRecorderState] = React.useState<RecorderState>(
    recorder?.state || "inactive"
  );

  const generateFileName = (storage: SessionStorage) => {
    const randomNum = Math.floor(Math.random() * 90000) + 10000;
    const timestamp = getSystemTime().getTime();
    const testEnv = storage?.file_destination?.test_env;
    return `${testEnv}/${randomNum}_${timestamp}.webm`;
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (data) {
      const fileName = generateFileName(storage);
      try {
        await createBlob({
          url: `/${fileName}?${storage?.sas_token}`,
          data,
          // eslint-disable-next-line
          alert_type_id: +storage?.file_destination.test_env!
        }).unwrap();
        try {
          const alertData = alertHelper(alertConfig).getAlert(
            AlertName.EnvironmentTestCaptureSuccess,
            fileName,
            null
          );
          await createAlert(alertData).unwrap();
        } catch (error: any) {
          handleError(`Error in environment capture success alert upload, ${error}`);
        }

        proceedToNextScreen();
      } catch (error: any) {
        handleError(`Error in environment video file upload, ${error}`);
        setIsSubmitted(false);
      }
    }
  };

  const handleStartRecording = () => {
    if (recorderState !== MediaRecorderState.Recording) {
      recorder?.start();
    }
    SetRecorderState(recorder?.state);
  };

  const [videoObjectUrl, setVideoObjectUrl] = useState<string>();

  useEffect(() => {
    if (!data) {
      setVideoObjectUrl(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(data);
    setVideoObjectUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [data]);

  const handleRetake = async () => {
    SetRecorderState("inactive");
    setIsSubmitted(false);
    await trackEventInAlvy(AlvyEventTypes.EnvTestRetake, AlvyEventState.Success);
  };

  useEffect(() => {
    (async () => {
      if (blobCreationError) {
        alertHelper(alertConfig).raiseAlert(AlertName.EnvironmentTestCaptureFailure, dispatch);
      }
    })();
  }, [blobCreationError]);

  return (
    <Base>
      {isDemoOpened && <ViewDemo onClick={() => SetIsDemoOpened(false)} />}
      {!isDemoOpened && (
        <>
          <Header
            title={<FormattedMessage id="page.EnvTestInstructions.header" />}
            titleClass="tvp-text-sm tvp-font-semibold tvp-clr-neutral tvp-uppercase tvp-ml-2.5"
            tabIndex={1}
          />
          <Content>
            {!mediaStream && <img src={Loader} alt="loader" />}
            {mediaStream && (
              <>
                <div className="tvp-flex tvp-mb-3 tvp-videoText">
                  {recorderState === MediaRecorderState.InActive && (
                    <p className="tvp-mr-2">
                      <FormattedMessage id="page.EnvTestCapture.videoInstuction.text" />
                    </p>
                  )}
                  {recorderState === MediaRecorderState.Recording && (
                    <p className="tvp-mr-2">
                      <Countdown duration={testEnvMaxDuration} />
                    </p>
                  )}
                  {recorderState === MediaRecorderState.Complete && (
                    <p id="environment-recording-complete" className="tvp-mr-2">
                      <FormattedMessage id="page.EnvTestCapture.recordingComplete.text" />
                    </p>
                  )}
                  {recorderState === MediaRecorderState.InActive && (
                    <Button
                      label={<FormattedMessage id="button.viewDemo" />}
                      tabIndex={2}
                      className={
                        "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-white tvp-clr-neutral tvp-bdr-green tvp-min-w-unset"
                      }
                      onClick={() => SetIsDemoOpened(true)}
                    />
                  )}
                </div>
                {recorderState !== MediaRecorderState.Complete ? (
                  !mediaStream ? (
                    <img src={Loader} alt="loader" />
                  ) : (
                    <div style={{ margin: "10px" }}>
                      <VideoPlayer
                        videoRef={videoRef}
                        mediaStream={mediaStream}
                        playerWidth={"200px"}
                      />
                    </div>
                  )
                ) : (
                  <>
                    <div className="tvp-envCheckRecordingComplete tvp-flex tvp-justify-center">
                      {videoObjectUrl && (
                        <video
                          aria-labelledby="environment-recording-complete"
                          controls
                          controlsList="play timeline volume nofullscreen"
                          src={videoObjectUrl}
                        />
                      )}
                    </div>
                    {blobCreationError && isSubmitted ? (
                      <div className="tvp-w-full tvp-flex tvp-justify-center tvp-items-center tvp-p-1 tvp-clr-red">
                        Error uploading file
                      </div>
                    ) : null}
                  </>
                )}
                {recorderState === MediaRecorderState.InActive && (
                  <p className="tvp-mt-3">
                    <FormattedMessage id="page.EnvTestCapture.instruction.text" />
                  </p>
                )}
                <div className="tvp-buttonPart">
                  {recorderState === MediaRecorderState.InActive && (
                    <Button
                      label={<FormattedMessage id="button.startRecording" />}
                      className={
                        "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-bg-green tvp-bdr-green"
                      }
                      onClick={handleStartRecording}
                    />
                  )}
                  {recorderState === MediaRecorderState.Recording && (
                    <Button
                      label={<FormattedMessage id="button.stopRecording" />}
                      className={
                        "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-bg-red tvp-bdr-red tvp-mt-9"
                      }
                      onClick={handleStopRecording}
                    />
                  )}
                </div>
                {recorderState === MediaRecorderState.Complete && (
                  <div className="tvp-buttonPart">
                    <Button
                      label={<FormattedMessage id="button.Retake" />}
                      className={
                        "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-white tvp-clr-neutral tvp-bdr-green tvp-m-1"
                      }
                      onClick={handleRetake}
                    />
                    <Button
                      disabled={isSubmitDisabled}
                      label={<FormattedMessage id="button.Submit" />}
                      className={
                        isSubmitDisabled
                          ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray tvp-w-150"
                          : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-bg-green tvp-bdr-green tvp-m-1"
                      }
                      onClick={handleSubmit}
                    />
                  </div>
                )}
              </>
            )}
          </Content>
          <FooterLogo />
        </>
      )}
    </Base>
  );
};

export default EnvTestCapture;
